<script lang="ts" setup>
import { ref } from 'vue';
import MagnifyIcon from 'vue-material-design-icons/Magnify.vue';
import TrendingUpIcon from 'vue-material-design-icons/TrendingUp.vue';
import { useFetch } from '#app/composables/fetch';
import { navigateTo } from '#app/composables/router';

type TabKey = 'recipe' | 'menu';

const currentTab = ref<TabKey>('recipe');

const tabItems: Array<{
  key: TabKey;
  text: string;
}> = [
  {
    key: 'recipe',
    text: 'レシピを探す',
  },
  {
    key: 'menu',
    text: '献立を探す',
  },
];

function changeTab(tab: TabKey) {
  currentTab.value = tab;
}

function createDynamicTabClass(tab: TabKey) {
  return {
    'text-gray-500': currentTab.value === tab,
  };
}

const searchKeyword = ref('');

const { data: popularKeywords } = useFetch(
  '/api/kiwi_user/recipe_popular_keywords/featured',
);

function submit(key?: TabKey) {
  switch (key) {
    case 'recipe':
      void navigateTo(`/public/article/recipe?s=${searchKeyword.value}`);
      break;
    case 'menu':
      void navigateTo(`/public/article/menu?s=${searchKeyword.value}`);
      break;
  }
}

function createKeywordLink(keyword: string) {
  return `/public/article/recipe?s=${keyword}`;
}
</script>

<template>
  <div>
    <!-- NOTE: PC幅とSP幅で表示内容がガラッと変わるため、HTML構造も別々にしている -->
    <div
      class="max-md:hidden flex bg-white shadow border border-gray-300 rounded p-4"
    >
      <form
        class="mr-4 pr-4 border-r border-gray-300 w-2/3 shrink-0"
        data-testid="pc-form"
        @submit.prevent="submit(currentTab)"
      >
        <div class="text-gray-400 leading-none mb-3 px-2">
          <button
            v-for="item in tabItems"
            :key="item.key"
            class="underline first:pr-3 first:mr-3 first:border-r border-gray-400"
            :class="createDynamicTabClass(item.key)"
            type="button"
            @click="changeTab(item.key)"
          >
            {{ item.text }}
          </button>
        </div>

        <div class="relative">
          <MagnifyIcon
            class="absolute top-1/2 left-3 transform -translate-y-1/2 text-gray-400"
            :size="20"
          />

          <input
            v-model="searchKeyword"
            placeholder="料理名、食材、目的"
            class="border border-gray-300 rounded-md text-lg pl-10 pr-3 py-2 w-full shadow-inner focus:bg-cream-200"
          />
        </div>
      </form>

      <div class="grow text-sm">
        <p
          class="font-bold text-center text-pink-400 flex items-center justify-center gap-1 mb-2"
        >
          <span>&#92;</span>
          <TrendingUpIcon :size="20" />
          <span>人気のキーワード</span>
          <span>&#47;</span>
        </p>

        <div class="flex flex-wrap gap-x-2 gap-y-1">
          <NuxtLink
            v-for="keyword in popularKeywords ?? []"
            :key="keyword"
            :to="createKeywordLink(keyword)"
            class="underline text-gray-500"
          >
            {{ keyword }}
          </NuxtLink>
        </div>
      </div>
    </div>

    <div class="md:hidden px-4 py-2 bg-cream-200">
      <form
        class="relative mb-2"
        data-testid="sp-form"
        @submit.prevent="submit('recipe')"
      >
        <MagnifyIcon
          class="absolute top-1/2 left-3 transform -translate-y-1/2 text-gray-400"
          :size="20"
        />

        <input
          v-model="searchKeyword"
          placeholder="料理名、食材、目的"
          class="border border-gray-300 rounded-md pl-10 pr-3 py-2 w-full shadow-inner focus:bg-cream-200"
        />
      </form>

      <div class="max-md:truncate text-sm">
        <span class="md:hidden font-bold text-brown-300 mr-2"
          >人気のキーワード:</span
        >
        <NuxtLink
          v-for="keyword in popularKeywords ?? []"
          :key="keyword"
          :to="createKeywordLink(keyword)"
          class="underline text-gray-500 mr-2"
        >
          {{ keyword }}
        </NuxtLink>
      </div>
    </div>
  </div>
</template>
